import { IntegrationsSetting } from '@goldcast/api/backend';

import { getDefaultState } from './index';
import {
  State,
  SolutionInstance,
  Authentication,
  SlackCurrentUser,
  CrmObjectType,
  SourceField,
  TargetField,
  FieldMapping,
  FieldMappingRowError,
  WistiaProject
} from './types';

import {
  getTargetFieldsDefaultState,
  getFieldMappingsDefaultState,
  getFieldMappingRowErrorDefaultState,
  getPrevFieldMappingsDefaultState,
  getSaveFieldMappingLoadingDefaultState
} from '@/studio/orgIntegration/store/utils';

export default {
  setInitializeIntegrationLoading(state: State, isInitializeIntegrationLoading: boolean) {
    state.isInitializeIntegrationLoading = isInitializeIntegrationLoading;
  },
  setTrayLiveSolutions(state: State, solutions) {
    state.trayLiveSolutions = solutions;
  },
  setTrayLiveSolutionsLoading(state: State, isTrayLiveSolutionsLoading: boolean) {
    state.isTrayLiveSolutionsLoading = isTrayLiveSolutionsLoading;
  },
  setTrayAuthentications(state: State, authentications: Authentication[]) {
    state.trayAuthentications = authentications;
  },
  setAuthenticationsLoading(state: State, isAuthenticationsLoading: boolean) {
    state.isAuthenticationsLoading = isAuthenticationsLoading;
  },
  setTraySolutionInstances(state: State, solutionInstances: SolutionInstance[]) {
    state.traySolutionInstances = solutionInstances;
  },
  setSolutionInstancesLoading(state: State, isSolutionInstancesLoading: boolean) {
    state.isSolutionInstancesLoading = isSolutionInstancesLoading;
  },
  setCurrentSolutionInstance(state: State, instance: SolutionInstance) {
    state.currentSolutionInstance = instance;
    state.currentCrmType = instance.solutionTitle.toUpperCase();
  },
  setCurrentSolutionIframeSrc(state: State, iframeSrc: string) {
    state.currentSolutionIframeSrc = iframeSrc;
  },
  setCurrentAuthentication(state: State, authentication: Authentication) {
    state.currentAuthentication = authentication;
  },
  setCurrentAuthenticationIframeSrc(state: State, iframeSrc) {
    state.currentAuthenticationIframeSrc = iframeSrc;
  },
  setIsTrayLoading(state: State, isTrayLoading: boolean) {
    state.isTrayLoading = isTrayLoading;
  },
  setTrayError(state: State, trayError) {
    state.trayError = trayError;
  },
  RESET_ORG_INTEGRATION_STORE(state: State) {
    Object.assign(state, getDefaultState());
  },
  setIntegrationSettingsLoading(state: State, isIntegrationSettingsLoading: boolean) {
    state.isIntegrationSettingsLoading = isIntegrationSettingsLoading;
  },
  setIntegrationSettings(state: State, integrationSettings: IntegrationsSetting[]) {
    state.integrationSettings = integrationSettings;
  },
  updateIntegrationSettingsItem(state: State, integrationSettings: IntegrationsSetting) {
    state.integrationSettings = state.integrationSettings.map(setting =>
      setting.id === integrationSettings.id ? integrationSettings : setting
    );
  },
  setSlackCurrentUser(state: State, slackCurrentUser: SlackCurrentUser | null) {
    state.slackCurrentUser = slackCurrentUser;
  },
  // Field Mapping ***********************************************************
  setCurrentCrmType(state: State, currentCrmType: string) {
    state.currentCrmType = currentCrmType;
  },
  setCrmObjectTypesLoading(state: State, isCrmObjectTypesLoading: boolean) {
    state.isCrmObjectTypesLoading = isCrmObjectTypesLoading;
  },
  setCrmObjectTypes(state: State, crmObjectTypes: CrmObjectType[]) {
    state.crmObjectTypes = crmObjectTypes;
  },
  setSourceFieldLoading(state: State, isSourceFieldLoading: boolean) {
    state.isSourceFieldLoading = isSourceFieldLoading;
  },
  setSourceFields(state: State, sourceFields: SourceField[]) {
    state.sourceFields = sourceFields;
  },
  setTargetFieldLoading(
    state: State,
    { crmObjectType, isTargetFieldLoading }: { crmObjectType: string; isTargetFieldLoading: boolean }
  ) {
    if (crmObjectType in state.targetFields) {
      state.targetFields[crmObjectType].isLoading = isTargetFieldLoading;
    }
  },
  setTargetFieldsError(
    state: State,
    { crmObjectType, targetFieldsError }: { crmObjectType: string; targetFieldsError: any }
  ) {
    if (crmObjectType in state.targetFields) {
      state.targetFields[crmObjectType].error = targetFieldsError;
    }
  },
  setTargetFields(
    state: State,
    { crmObjectType, targetFields }: { crmObjectType: string; targetFields: TargetField[] }
  ) {
    if (crmObjectType in state.targetFields) {
      state.targetFields[crmObjectType].fields = targetFields;
    }
  },
  initializeFieldMappingStore(state: State) {
    state.targetFields = getTargetFieldsDefaultState(state.crmObjectTypes);
    state.fieldMappings = getFieldMappingsDefaultState(state.crmObjectTypes);
    state.prevFieldMappings = getPrevFieldMappingsDefaultState(state.crmObjectTypes);
    state.fieldMappingRowError = getFieldMappingRowErrorDefaultState(state.crmObjectTypes);
    state.isSaveFieldMappingLoading = getSaveFieldMappingLoadingDefaultState(state.crmObjectTypes);
    state.customFieldMappings = getFieldMappingsDefaultState(state.crmObjectTypes);
  },
  setFieldMappingLoading(
    state: State,
    { crmObjectType, isFieldMappingLoading }: { crmObjectType: string; isFieldMappingLoading: boolean }
  ) {
    if (crmObjectType && crmObjectType in state.fieldMappings) {
      state.fieldMappings[crmObjectType].isLoading = isFieldMappingLoading;
    } else {
      state.crmObjectTypes.forEach(({ crm_object_type }) => {
        state.fieldMappings[crm_object_type].isLoading = isFieldMappingLoading;
      });
    }
  },
  setFieldMappings(
    state: State,
    { crmObjectType, fieldMappings }: { crmObjectType: string; fieldMappings: FieldMapping[] }
  ) {
    if (crmObjectType in state.fieldMappings) {
      state.fieldMappings[crmObjectType].mappings = fieldMappings;
    }
  },
  setCustomFieldMappings(
    state: State,
    { crmObjectType, customFieldMappings }: { crmObjectType: string; customFieldMappings: FieldMapping[] }
  ) {
    if (crmObjectType in state.customFieldMappings) {
      state.customFieldMappings[crmObjectType].mappings = customFieldMappings;
    }
  },
  setPrevFieldMappings(
    state: State,
    { crmObjectType, prevFieldMappings }: { crmObjectType: string; prevFieldMappings: FieldMapping[] }
  ) {
    state.prevFieldMappings[crmObjectType] = prevFieldMappings;
  },
  addNewEmptyFieldMappingItem(
    state: State,
    { crmObjectType, emptyFieldMapping }: { crmObjectType: string; emptyFieldMapping: FieldMapping }
  ) {
    if (crmObjectType in state.fieldMappings) {
      state.fieldMappings[crmObjectType].mappings = [...state.fieldMappings[crmObjectType].mappings, emptyFieldMapping];
    }
  },
  removeFieldMappingItem(
    state: State,
    { crmObjectType, fieldMappingID }: { crmObjectType: string; fieldMappingID: string }
  ) {
    if (crmObjectType in state.fieldMappings) {
      state.fieldMappings[crmObjectType].mappings = state.fieldMappings[crmObjectType].mappings.filter(
        fieldMapping => fieldMapping.id !== fieldMappingID
      );
    }
  },
  updateFieldMappingItem(
    state: State,
    { crmObjectType, updatedFieldMapping }: { crmObjectType: string; updatedFieldMapping: FieldMapping }
  ) {
    if (crmObjectType in state.fieldMappings) {
      state.fieldMappings[crmObjectType].mappings = state.fieldMappings[crmObjectType].mappings.map(fieldMapping => {
        if (fieldMapping.id === updatedFieldMapping.id) {
          return {
            ...fieldMapping,
            ...updatedFieldMapping
          };
        }
        return fieldMapping;
      });
    }
  },
  resetFieldMappingStore(state: State) {
    state.crmObjectTypes = [];
    state.isCrmObjectTypesLoading = false;
    state.isSourceFieldLoading = false;
    state.targetFields = {};
    state.fieldMappings = {};
    state.prevFieldMappings = {};
    state.fieldMappingRowError = {};
  },
  setFieldMappingRowError(
    state: State,
    { crmObjectType, errorsArray }: { crmObjectType: string; errorsArray: (FieldMappingRowError | null)[] }
  ) {
    state.fieldMappingRowError[crmObjectType] = errorsArray;
  },
  discardFieldMappingChanges(state: State, crmObjectType: string) {
    if (!state?.fieldMappings[crmObjectType]?.mappings || !state?.prevFieldMappings[crmObjectType]) {
      return;
    }
    state.fieldMappings[crmObjectType].mappings = state.prevFieldMappings[crmObjectType];
    const defaultErrorArray = new Array(state.fieldMappings[crmObjectType].mappings.length).fill(null);
    state.fieldMappingRowError[crmObjectType] = defaultErrorArray;
  },
  setSaveFieldMappingLoading(
    state: State,
    { crmObjectType, isSaveFieldMappingLoading }: { crmObjectType: string; isSaveFieldMappingLoading: boolean }
  ) {
    state.isSaveFieldMappingLoading[crmObjectType] = isSaveFieldMappingLoading;
  },
  setIsValidEloquaAuthConnected(state: State, isValid: boolean) {
    state.isValidEloquaAuthConnected = isValid;
  },
  setIsEloquaAuthHealthApiLoading(state: State, isApiLoading: boolean) {
    state.isEloquaAuthHealthApiLoading = isApiLoading;
  },
  setIsPrePopulateRequiredMappingsInProgress(state: State, isUpdating: boolean) {
    state.isPrePopulateRequiredMappingsInProgress = isUpdating;
  },
  setWistiaProjects(state: State, projects: WistiaProject[]) {
    state.wistiaProjects = projects;
  },
  setIsWistiaProjectsLoading(state: State, isLoading: boolean) {
    state.isWistiaProjectsLoading = isLoading;
  },
  setIsValidateCampaignIdOrgLoading(state: State, isLoading: boolean) {
    state.isSalesforceValidateCampaignIdOrgLoading = isLoading;
  },
  setIsCampaignStatusOrgLoading(state: State, isLoading: boolean) {
    state.isSalesforceCampaignStatusOrgLoading = isLoading;
  },
  setConnectSalesforceCampaignOrgLoading(state: State, isLoading: boolean) {
    state.isSalesforceConnectCampaignOrgLoading = isLoading;
  }
};
